/**
 * @file Google Analytics 4 (gtag.js)
 * @description Carrega GA4 globalmente (pageviews / enhanced measurement).
 *
 * Key exports:
 * - initGa4() - Injeta gtag.js; dataLayer já sanitizado em index.tsx
 *
 * qualify_lead é enviado server-side em auth-signup (Measurement Protocol).
 */

import { ensureDataLayerSanitizer } from './sanitizeDataLayer';

const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID ?? '';

let initialized = false;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export function isGa4Enabled(): boolean {
  return Boolean(measurementId);
}

function loadGtagScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`,
    );
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load GA4 script'));
    document.head.appendChild(script);
  });
}

/** Inicializa dataLayer + gtag e carrega o script de forma assíncrona. */
export function initGa4(): void {
  if (initialized || !isGa4Enabled()) return;

  // Sanitizer já instalado em index.tsx; só garante array para o stub gtag.
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId);

  void loadGtagScript()
    .then(() => {
      // gtag.js pode substituir push — reaplicar uma vez se necessário
      ensureDataLayerSanitizer();
    })
    .catch((err) => {
      if (import.meta.env.DEV) {
        console.warn('[GA4] Failed to load gtag.js:', err);
      }
    });

  initialized = true;
}
